import React, { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import { db } from "../firebase";
import VolatilityHeader from "./VolatilityHeader";
import "./VolatilityHistory.css";
import BottomNavVolatility from "./BottomNavVolatility";

const FILTERS = ["ALL", "OPEN", "WON", "LOST", "CLOSED"];

const VolatilityHistory = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const username = localStorage.getItem("username");

    if (!username) {
      setOrders([]);
      setLoading(false);
      setError("Username haijapatikana. Tafadhali ingia tena.");
      return undefined;
    }

    setLoading(true);
    setError("");

    const ordersQuery = query(
      collection(db, "volatilityorder"),
      where("username", "==", username)
    );

    const unsubscribe = onSnapshot(
      ordersQuery,
      (snapshot) => {
        const fetchedOrders = snapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));

        fetchedOrders.sort((a, b) => {
          const dateA = getOrderTime(a);
          const dateB = getOrderTime(b);
          return dateB - dateA;
        });

        setOrders(fetchedOrders);
        setLoading(false);
        setError("");
      },
      (snapshotError) => {
        console.error("Error loading volatility history:", snapshotError);
        setError("Imeshindikana kupakia historia. Tafadhali jaribu tena.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((previous) => previous + 1);
  };

  const getStatus = (order) => {
    const rawStatus = String(
      order.status || order.result || "OPEN"
    )
      .trim()
      .toUpperCase();

    if (["WIN", "WON", "PROFIT", "SUCCESS"].includes(rawStatus)) {
      return "WON";
    }

    if (["LOSS", "LOST", "FAILED"].includes(rawStatus)) {
      return "LOST";
    }

    if (
      ["CLOSED", "CLOSE", "COMPLETED", "EXPIRED"].includes(rawStatus)
    ) {
      return "CLOSED";
    }

    if (
      ["OPEN", "PENDING", "ACTIVE", "RUNNING"].includes(rawStatus)
    ) {
      return "OPEN";
    }

    return rawStatus || "OPEN";
  };

  const filteredOrders = orders.filter((order) => {
    if (filter === "ALL") return true;
    return getStatus(order) === filter;
  });

  const totalOrders = orders.length;

  const openOrders = orders.filter(
    (order) => getStatus(order) === "OPEN"
  ).length;

  const wonOrders = orders.filter(
    (order) => getStatus(order) === "WON"
  ).length;

  const lostOrders = orders.filter(
    (order) => getStatus(order) === "LOST"
  ).length;

  const getAmount = (order) => {
    const amount =
      order.amount ??
      order.stake ??
      order.investment ??
      order.tradeAmount ??
      0;

    const numericAmount = Number(amount);
    return Number.isFinite(numericAmount) ? numericAmount : 0;
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (order) => {
    const possibleDate =
      order.createdAt ??
      order.timestamp ??
      order.date ??
      order.created_at ??
      order.time;

    if (!possibleDate) return "—";

    let date;

    if (typeof possibleDate?.toDate === "function") {
      date = possibleDate.toDate();
    } else if (
      typeof possibleDate === "number" ||
      typeof possibleDate === "string"
    ) {
      date = new Date(possibleDate);
    } else if (possibleDate?.seconds) {
      date = new Date(possibleDate.seconds * 1000);
    } else {
      return "—";
    }

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString();
  };

  const getOrderTime = (order) => {
    const possibleDate =
      order.createdAt ??
      order.timestamp ??
      order.date ??
      order.created_at ??
      order.time;

    if (typeof possibleDate?.toDate === "function") {
      return possibleDate.toDate().getTime();
    }

    if (typeof possibleDate?.seconds === "number") {
      return possibleDate.seconds * 1000;
    }

    if (
      typeof possibleDate === "string" ||
      typeof possibleDate === "number"
    ) {
      const time = new Date(possibleDate).getTime();
      return Number.isNaN(time) ? 0 : time;
    }

    return 0;
  };

  const getStatusClass = (status) => {
    return `status-${String(status)
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "")}`;
  };

  return (
    <div className="history-page">
      <VolatilityHeader />

      <main className="history-container">
        <div className="history-heading">
          <div className="history-heading-content">
            <button
              type="button"
              className="history-back"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              ←
            </button>

            <div>
              <h1>Trade History</h1>
              <p>Angalia historia ya trades zako</p>
            </div>
          </div>

          <button
            type="button"
            className="history-refresh"
            onClick={handleRefresh}
            disabled={loading}
          >
            ↻ <span>Refresh</span>
          </button>
        </div>

        <section className="history-summary">
          <div className="history-summary-card">
            <span className="history-summary-label">Total Trades</span>
            <strong>{totalOrders}</strong>
          </div>

          <div className="history-summary-card">
            <span className="history-summary-label">Open Trades</span>
            <strong>{openOrders}</strong>
          </div>

          <div className="history-summary-card">
            <span className="history-summary-label">Won Trades</span>
            <strong>{wonOrders}</strong>
          </div>

          <div className="history-summary-card">
            <span className="history-summary-label">Lost Trades</span>
            <strong>{lostOrders}</strong>
          </div>
        </section>

        <section className="history-content">
          <div className="history-section-heading">
            <div>
              <h2>Recent Trades</h2>
              <p>Historia ya miamala yako</p>
            </div>

            <span className="history-count">
              {filteredOrders.length} trades
            </span>
          </div>

          <div className="history-filters">
            {FILTERS.map((value) => (
              <button
                type="button"
                key={value}
                className={
                  filter === value
                    ? "history-filter active"
                    : "history-filter"
                }
                onClick={() => setFilter(value)}
              >
                {value}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="history-message">
              <span className="history-spinner" />
              <p>Inapakia trade history...</p>
            </div>
          ) : error ? (
            <div className="history-message history-error">
              <span className="history-message-icon">!</span>
              <p>{error}</p>
              <button type="button" onClick={handleRefresh}>
                Jaribu tena
              </button>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="history-empty">
              <div className="history-empty-icon">↗</div>
              <h3>Hakuna trades zilizopatikana</h3>
              <p>
                {filter === "ALL"
                  ? "Historia ya trades zako itaonekana hapa."
                  : `Hakuna trades zenye status ya ${filter}.`}
              </p>

              {filter !== "ALL" && (
                <button
                  type="button"
                  className="history-empty-button"
                  onClick={() => setFilter("ALL")}
                >
                  Angalia trades zote
                </button>
              )}
            </div>
          ) : (
            <div className="history-table-wrapper">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Trade ID</th>
                    <th>Date &amp; Time</th>
                    <th>Asset / Pair</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredOrders.map((order) => {
                    const status = getStatus(order);

                    return (
                      <tr key={order.id}>
                        <td>
                          <span className="history-trade-id">
                            {String(
                              order.tradeId ||
                                order.orderId ||
                                order.id
                            ).slice(0, 12)}
                          </span>
                        </td>

                        <td className="history-date">
                          {formatDate(order)}
                        </td>

                        <td className="history-asset">
                          {order.asset ||
                            order.pair ||
                            order.symbol ||
                            order.market ||
                            "Volatility"}
                        </td>

                        <td>
                          {order.type ||
                            order.direction ||
                            order.tradeType ||
                            "—"}
                        </td>

                        <td className="history-amount">
                          {formatAmount(getAmount(order))}
                        </td>

                        <td>
                          <span
                            className={`history-status ${getStatusClass(
                              status
                            )}`}
                          >
                            <span className="history-status-dot" />
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="history-footer-note">
          Trade history yako husasishwa moja kwa moja.
        </p>
      </main>

      <BottomNavVolatility />
    </div>
  );
};

export default VolatilityHistory;